<template>
	<Col :span="24" :offset="0">
		<div class="flex-wrapper scrollable-x">
			<TypographyTitle class="flex-grow" :level="3">{{ $t('account.externalApiKeys.header') }}</TypographyTitle>
			<Button type="primary" @click="openCreateModal">{{ $t('account.externalApiKeys.addButtonLabel') }}</Button>
		</div>
		<div class="scrollable-x">
			<Table :columns="columns" :data-source="keys" :pagination="false" :loading="isLoading" row-key="id">
				<template #bodyCell="{ column, record }">
					<template v-if="column.dataIndex === 'status'">
						<StatusDisplay :source="record.status" prefix="account.externalApiKeys.status" :badge-dict="statusBadgeDict" />
					</template>
					<template v-else-if="column.dataIndex === 'permissions'">
						{{ formatPermissions(record.permissions) }}
					</template>
					<template v-else-if="column.dataIndex === 'name'">
						<Button
							v-if="!record.isRevoked"
							type="link"
							size="small"
							@click="openUpdatePermissionsModal(record)"
						>
							{{ record.name }}
						</Button>
						<span v-else>{{ record.name }}</span>
					</template>
					<template v-else-if="column.dataIndex === 'lastUsedAt'">
						<DateTimeDisplay :date="record.lastUsedAt" />
					</template>
					<template v-else-if="column.dataIndex === 'isEnabled'">
						<Switch
							:checked="!record.isRevoked && record.isEnabled"
							:disabled="record.isRevoked"
							size="small"
							@change="checked => keySetEnabled(record, checked)"
						/>
					</template>
					<template v-else-if="column.dataIndex === 'createdAt'">
						<DateTimeDisplay :date="record.createdAt" />
					</template>
					<template v-else-if="column.dataIndex === 'revokedAt'">
						<DateTimeDisplay :date="record.revokedAt" />
					</template>
					<template v-else-if="column.dataIndex === 'actions'">
						<Space>
							<PopconfirmButton
								v-if="!record.isRevoked"
								:title="$t('account.externalApiKeys.revokeConfirmation')"
								:ok-text="$t('account.externalApiKeys.revokeButtonLabel')"
								:cancel-text="$t('account.externalApiKeys.cancelButtonLabel')"
								size="small"
								:onConfirm="() => keyRevoke(record)"
							>
								{{ $t('account.externalApiKeys.revokeButtonLabel') }}
							</PopconfirmButton>
							<PopconfirmButton
								:title="$t('account.externalApiKeys.deleteConfirmation')"
								:ok-text="$t('account.externalApiKeys.deleteButtonLabel')"
								:cancel-text="$t('account.externalApiKeys.cancelButtonLabel')"
								size="small"
								:onConfirm="() => keyDelete(record)"
							>
								{{ $t('account.externalApiKeys.deleteButtonLabel') }}
							</PopconfirmButton>
						</Space>
					</template>
				</template>
			</Table>
		</div>
	</Col>

	<ExternalApiKeyCreateModal
		:visible="createModalVisible"
		@close="closeCreateModal"
		@created="onCreated"
	/>

	<ExternalApiKeyCreatedModal
		:visible="createdModalVisible"
		:raw-key="createdRawKey"
		@close="closeCreatedModal"
	/>

	<ExternalApiKeyUpdatePermissionsModal
		:visible="updatePermissionsModalVisible"
		:api-key="selectedKeyForPermissionsEdit"
		@close="closeUpdatePermissionsModal"
		@updated="reload"
	/>
</template>

<script>
import { onMounted, ref } from 'vue';
import { Button, Col, message, Space, Switch, Table, TypographyTitle } from 'ant-design-vue';

import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import { toCamelCaseList } from '@/api/ApiHelpers';
import Consts from '@/api/Consts';

import DateTimeDisplay from '@/components/common/DateTimeDisplay.vue';
import StatusDisplay from '@/components/common/StatusDisplay.vue';
import { showErrorMessage } from '@/components/common/errorMessage';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';
import ExternalApiKeyCreateModal from '@/components/account/ExternalApiKeyCreateModal.vue';
import ExternalApiKeyCreatedModal from '@/components/account/ExternalApiKeyCreatedModal.vue';
import ExternalApiKeyUpdatePermissionsModal from '@/components/account/ExternalApiKeyUpdatePermissionsModal.vue';


export default {
	components: {
		Button, Col, Space, Switch, Table, TypographyTitle, DateTimeDisplay, StatusDisplay, PopconfirmButton, ExternalApiKeyCreateModal, ExternalApiKeyCreatedModal, ExternalApiKeyUpdatePermissionsModal,
	},
	setup() {
		const { t } = useI18n();
		const isLoading = ref(false);
		const keys = ref([]);
		const createModalVisible = ref(false);
		const createdModalVisible = ref(false);
		const createdRawKey = ref('');
		const updatePermissionsModalVisible = ref(false);
		const selectedKeyForPermissionsEdit = ref(null);

		const reload = () => {
			isLoading.value = true;
			Api.externalApiKeyGetList(data => {
				keys.value = toCamelCaseList(data.keys).map(key => {
					let status = 'active';
					if (key.isRevoked) {
						status = 'revoked';
					} else if (!key.isEnabled) {
						status = 'disabled';
					}
					return {
						...key,
						status,
					};
				});
				isLoading.value = false;
			});
		};

		onMounted(reload);

		const formatPermissions = permissions => {
			if (permissions == null || permissions.length === 0) {
				return t('account.externalApiKeys.noPermissions');
			}
			const labels = permissions.map(permission => {
				const key = permission === 'base_stations' ? 'baseStations' : permission;
				return t('account.externalApiKeys.permissionLabels.' + key);
			});
			return labels.join(', ');
		};

		const statusBadgeDict = ref({
			active: 'success',
			revoked: 'default',
			disabled: 'default',
		});

		const closeCreateModal = () => {
			createModalVisible.value = false;
		};

		const openCreateModal = () => {
			createModalVisible.value = true;
		};

		const closeCreatedModal = () => {
			createdModalVisible.value = false;
			createdRawKey.value = '';
		};

		const closeUpdatePermissionsModal = () => {
			updatePermissionsModalVisible.value = false;
			selectedKeyForPermissionsEdit.value = null;
		};

		const openUpdatePermissionsModal = record => {
			selectedKeyForPermissionsEdit.value = record;
			updatePermissionsModalVisible.value = true;
		};

		const onCreated = rawKey => {
			createModalVisible.value = false;
			createdRawKey.value = rawKey;
			createdModalVisible.value = true;
			reload();
		};

		const keyRevoke = record => {
			isLoading.value = true;
			Api.externalApiKeyRevoke(record.id, response => {
				if (response.status === 'ok') {
					message.success(t('account.externalApiKeys.revokeSuccess'));
				} else {
					showErrorMessage(response.errors);
				}
				reload();
			});
		};

		const keyDelete = record => {
			isLoading.value = true;
			Api.externalApiKeyDelete(record.id, response => {
				if (response.status === 'ok') {
					message.success(t('account.externalApiKeys.deleteSuccess'));
				} else {
					showErrorMessage(response.errors);
				}
				reload();
			});
		};

		const keySetEnabled = (record, checked) => {
			const previousEnabled = record.isEnabled;
			const previousStatus = record.status;
			record.isEnabled = checked;
			if (!record.isRevoked) {
				record.status = checked ? 'active' : 'disabled';
			}
			Api.externalApiKeySetEnabled(record.id, checked, response => {
				if (response.status !== 'ok') {
					record.isEnabled = previousEnabled;
					record.status = previousStatus;
					showErrorMessage(response.errors);
				}
			});
		};

		return {
			isLoading,
			keys,
			columns: Consts.externalApiKeysTableColumnsComp,
			createModalVisible,
			createdModalVisible,
			createdRawKey,
			updatePermissionsModalVisible,
			selectedKeyForPermissionsEdit,
			openCreateModal,
			closeCreateModal,
			closeCreatedModal,
			closeUpdatePermissionsModal,
			onCreated,
			openUpdatePermissionsModal,
			keyRevoke,
			keyDelete,
			keySetEnabled,
			formatPermissions,
			statusBadgeDict,
			reload,
		};
	},
}
</script>
